import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Button, Badge, Modal, Form, Spinner, Alert, Card, Tabs, Tab, InputGroup } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import * as XLSX from 'xlsx';

// Import custom components for file imports and creation
import AssociationImportModal from './AssociationImportModal';
import MemberImportModal from './MemberImportModal';
import AssociationCreationModal from './AssociationCreationModal';

const UserManagement = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Debug: Log user role to verify
  useEffect(() => {
    console.log('Current user in UserManagement:', user);
    console.log('User role:', user?.role);
  }, [user]);
  
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showDeleteAllAssociationsModal, setShowDeleteAllAssociationsModal] = useState(false);
  const [showDeleteAllMembersModal, setShowDeleteAllMembersModal] = useState(false);
  const [deleteAllMembersConfirmCode, setDeleteAllMembersConfirmCode] = useState('');

  // Form states
  const [currentUser, setCurrentUser] = useState({});
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'association',
    is_active: true,
    discipline: '',
    sendEmail: false
  });

  const [associations, setAssociations] = useState([]);
  // Default tab: 'users' for admin/regional, 'associations' for territorial
  const [activeTab, setActiveTab] = useState(
    user && user.role === 'uisp_territorial' ? 'associations' : 'users'
  );
  const [membersPage, setMembersPage] = useState(1);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersTotalPages, setMembersTotalPages] = useState(0);

  // Association pagination states
  const [associationsPage, setAssociationsPage] = useState(1);
  const [associationsLoading, setAssociationsLoading] = useState(false);
  const [associationsTotalPages, setAssociationsTotalPages] = useState(0);

  // Search and filter states
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  
  const [memberSearch, setMemberSearch] = useState('');
  const [debouncedMemberSearch, setDebouncedMemberSearch] = useState('');
  
  const [associationSearch, setAssociationSearch] = useState('');
  const [debouncedAssociationSearch, setDebouncedAssociationSearch] = useState('');

  // Total counts from API
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalAssociations, setTotalAssociations] = useState(0);
  const [totalMembers, setTotalMembers] = useState(0);

  // Detail view states
  const [showMemberDetail, setShowMemberDetail] = useState(false);
  const [showAssociationDetail, setShowAssociationDetail] = useState(false);
  const [currentMember, setCurrentMember] = useState(null);
  const [currentAssociation, setCurrentAssociation] = useState(null);

  // Import states for new modal approach
  const [showMemberImportModal, setShowMemberImportModal] = useState(false);
  const [showAssociationImportModal, setShowAssociationImportModal] = useState(false);

  // Manual creation modal states
  const [showAssociationCreateModal, setShowAssociationCreateModal] = useState(false);
  const [currentEditAssociation, setCurrentEditAssociation] = useState(null);

  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');

  // Pagination 
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);

  // Role options
  const roleOptions = [
    { value: 'admin', label: 'Amministratore' },
    { value: 'uisp_national', label: 'UISP Nazionale' },
    { value: 'uisp_regional', label: 'UISP Regionale' },
    { value: 'uisp_territorial', label: 'UISP Territoriale' },
    { value: 'association', label: 'Associazione' },
    { value: 'sda_territorial', label: 'Responsabile SDA Territoriale' },
    { value: 'sda_regional', label: 'Responsabile SDA Regionale' },
    { value: 'sda_nazionale', label: 'Responsabile SDA Nazionale' }
  ];
  
  // Opzioni per le discipline (categorie di eventi)
  const disciplineOptions = [
    { value: 'danza', label: 'Danza' },
    { value: 'manifestazione', label: 'Manifestazione' },
    { value: 'stage', label: 'Stage' },
    { value: 'acquaviva', label: 'Acquaviva' },
    { value: 'atletica leggera', label: 'Atletica Leggera' },
    { value: 'calcio', label: 'Calcio' },
    { value: 'ciclismo', label: 'Ciclismo' },
    { value: 'discipline orientali', label: 'Discipline Orientali' },
    { value: 'equestri e cinofile', label: 'Equestri e Cinofile' },
    { value: 'ginnastiche', label: 'Ginnastiche' },
    { value: 'giochi', label: 'Giochi' },
    { value: 'montagna', label: 'Montagna' },
    { value: 'motorismo', label: 'Motorismo' },
    { value: 'neve', label: 'Neve' },
    { value: 'nuoto', label: 'Nuoto' },
    { value: 'pallacanestro', label: 'Pallacanestro' },
    { value: 'pallavolo', label: 'Pallavolo' },
    { value: 'pattinaggio', label: 'Pattinaggio' },
    { value: 'subacquea', label: 'Subacquea' },
    { value: 'tennis', label: 'Tennis' },
    { value: 'vela', label: 'Vela' },
    { value: 'multi-sport', label: 'Multi-Sport' },
    { value: 'corso', label: 'Corso' },
    { value: 'altro', label: 'Altro' }
  ];

  // Fetch users and associations on component mount and when page changes
  useEffect(() => {
    // Don't fetch users if the user is uisp_territorial (they shouldn't see this tab)
    if (activeTab === 'users' && user && user.role !== 'uisp_territorial') {
      fetchUsers();
    } else if (activeTab === 'associations') {
      fetchAssociations();
    }
  }, [activeTab, page, user, debouncedUserSearch]);

  // Fetch members when tab changes or page changes
  useEffect(() => {
    if (activeTab === 'members') {
      fetchMembers();
    }
  }, [activeTab, membersPage, debouncedMemberSearch]);

  // Fetch associations when associations tab is active or page changes
  useEffect(() => {
    if (activeTab === 'associations') {
      fetchAssociations();
    }
  }, [activeTab, associationsPage, debouncedAssociationSearch]);

  // Debounced search effect for users
  useEffect(() => {
    // Don't search users if the user is uisp_territorial (they shouldn't see this tab)
    if (activeTab !== 'users') return;
    if (user && user.role === 'uisp_territorial') return;
    
    const debounceTimer = setTimeout(() => {
      setPage(1); // Reset to page 1 when searching
      setDebouncedUserSearch(userSearch);
    }, 500); // 500ms debounce

    return () => clearTimeout(debounceTimer);
  }, [userSearch, user, activeTab]);

  // Debounced search effect for associations
  useEffect(() => {
    if (activeTab !== 'associations') return;
    
    const debounceTimer = setTimeout(() => {
      setAssociationsPage(1); // Reset to page 1 when searching
      setDebouncedAssociationSearch(associationSearch);
    }, 500); // 500ms debounce

    return () => clearTimeout(debounceTimer);
  }, [associationSearch, activeTab]);

  // Debounced search effect for members
  useEffect(() => {
    if (activeTab !== 'members') return;
    
    const debounceTimer = setTimeout(() => {
      setMembersPage(1); // Reset to page 1 when searching
      setDebouncedMemberSearch(memberSearch);
    }, 500); // 500ms debounce

    return () => clearTimeout(debounceTimer);
  }, [memberSearch, activeTab]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.get('/admin/users', {
        params: {
          page: page,
          limit: limit,
          search: debouncedUserSearch // Add search parameter
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Frontend received users:', response.data);
      setUsers(response.data.users || []);
      setTotalUsers(response.data.total || 0);
      setTotalPages(Math.ceil(response.data.total / limit));
      setLoading(false);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Errore nel caricamento degli utenti. Verifica la connessione e i permessi.');
      setLoading(false);

      if (err.response && err.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  };

  const fetchAssociations = async () => {
    try {
      setAssociationsLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      // Using our new dedicated associations endpoint
      const response = await api.get('/admin/associations', {
        params: {
          page: associationsPage,
          limit: limit,
          search: debouncedAssociationSearch // Add search parameter
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Frontend received associations:', response.data.associations);
      setAssociations(response.data.associations || []);
      setTotalAssociations(response.data.total || 0);
      setAssociationsTotalPages(Math.ceil(response.data.total / limit));
      setAssociationsLoading(false);
    } catch (err) {
      console.error('Error fetching associations:', err);
      setAssociationsLoading(false);
      setError('Errore nel caricamento delle associazioni. Verifica la connessione e i permessi.');

      if (err.response && err.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  };

  const fetchMembers = async () => {
    try {
      setMembersLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.get('/admin/members', {
        params: {
          page: membersPage,
          limit,
          search: debouncedMemberSearch // Add search parameter
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Frontend received members:', response.data);
      setMembers(response.data.members || []);
      setTotalMembers(response.data.total || 0);
      setMembersTotalPages(Math.ceil(response.data.total / limit));
      setMembersLoading(false);
    } catch (err) {
      console.error('Error fetching members:', err);
      setError('Errore nel caricamento dei soci. Verifica la connessione e i permessi.');
      setMembersLoading(false);

      if (err.response && err.response.status === 401) {
        localStorage.removeItem('token');
        navigate('/login');
      }
    }
  };

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.username || formData.username.length < 3) {
      errors.username = 'Il nome utente deve essere di almeno 3 caratteri.';
    }

    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Inserisci un indirizzo email valido.';
    }

    if (showCreateModal) {
      if (!formData.password || formData.password.length < 6) {
        errors.password = 'La password deve essere di almeno 6 caratteri.';
      }

      if (formData.password !== formData.confirmPassword) {
        errors.confirmPassword = 'Le password non corrispondono.';
      }
    }

    // Verifica il campo disciplina per i ruoli SDA
    if (isSdaRole(formData.role) && !formData.discipline) {
      errors.discipline = 'Seleziona una disciplina per il responsabile SDA.';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Check if role is a SDA role (for showing/hiding discipline field)
  const isSdaRole = (role) => {
    return role?.includes('sda_');
  };

  // Handle pagination based on active tab
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Modal handlers
  const openCreateModal = () => {
    // Se siamo nella tab associazioni, impostiamo di default il ruolo a 'association'
    const defaultRole = activeTab === 'associations' ? 'association' : 'association';

    setFormData({
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
      role: defaultRole,
      discipline: '',
      is_active: true,
      sendEmail: false
    });
    setFormErrors({});
    setShowCreateModal(true);
  };

  const openEditModal = (user) => {
    setCurrentUser(user);
    setFormData({
      ...user,
      password: '',
      confirmPassword: '',
      discipline: user.discipline || ''
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const openDeleteModal = (user) => {
    setCurrentUser(user);
    setShowDeleteModal(true);
  };

  const openResetPasswordModal = (user) => {
    setCurrentUser(user);
    setFormData({
      ...formData,
      password: '',
      confirmPassword: ''
    });
    setFormErrors({});
    setShowResetPasswordModal(true);
  };

  // Import is now handled by specialized modals
  const handleOpenMemberImport = () => {
    setShowMemberImportModal(true);
  };

  const handleOpenAssociationImport = () => {
    setShowAssociationImportModal(true);
  };

  // Handler for successful member import
  const handleMemberImportSuccess = (data) => {
    console.log('Member import successful:', data);
    // Refresh the members list if on members tab
    if (activeTab === 'members') {
      fetchMembers();
    }
    setSuccessMessage('Importazione soci completata con successo!');
  };

  // Handler for successful association import
  const handleAssociationImportSuccess = (data) => {
    console.log('Association import successful:', data);
    // Refresh the associations list
    fetchAssociations();
    fetchUsers();
  };

  // Handlers for manual association creation
  const handleOpenAssociationCreate = () => {
    setCurrentEditAssociation(null); // Ensure we're creating, not editing
    setShowAssociationCreateModal(true);
  };

  const handleOpenAssociationEdit = (association) => {
    setCurrentEditAssociation(association);
    setShowAssociationCreateModal(true);
  };

  // Handler for successful association creation or edit
  const handleAssociationCreateSuccess = (association) => {
    console.log('Association created/updated successfully:', association);
    // Refresh the associations list
    fetchAssociations();
    setSuccessMessage(currentEditAssociation ? 'Associazione aggiornata con successo!' : 'Associazione creata con successo!');
    setSuccessMessage('Importazione associazioni completata con successo!');
  };

  // Action handlers
  const handleCreateUser = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const userData = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
        is_active: formData.is_active,
        sendEmail: formData.sendEmail || false
      };
      
      // Aggiungiamo la disciplina solo se il ruolo è un ruolo SDA
      if (isSdaRole(formData.role) && formData.discipline) {
        userData.discipline = formData.discipline;
      }

      const response = await api.post('/admin/users', userData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('User created:', response.data);
      setShowCreateModal(false);
      setSuccessMessage('Utente creato con successo!');
      fetchUsers();
    } catch (err) {
      console.error('Error creating user:', err);
      setError('Errore nella creazione dell\'utente. ' + (err.response?.data?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      await api.delete(`/admin/users/${currentUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowDeleteModal(false);
      setSuccessMessage('Utente eliminato con successo!');

      // Refresh the appropriate list based on active tab
      if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'associations') {
        fetchAssociations();
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Errore nell\'eliminazione dell\'utente.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (id) => {
    try {
      setMembersLoading(true);
      const token = localStorage.getItem('token');

      await api.delete(`/admin/members/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccessMessage('Socio eliminato con successo!');
      fetchMembers();
    } catch (err) {
      console.error('Error deleting member:', err);
      setError('Errore nell\'eliminazione del socio.');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleDeleteAllAssociations = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      await api.delete('/admin/associations', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowDeleteAllAssociationsModal(false);
      setSuccessMessage('Tutte le associazioni sono state eliminate con successo!');

      // Reset data
      setAssociations([]);
      // Refresh the users list too as some might have been removed
      fetchUsers();
    } catch (err) {
      console.error('Error deleting all associations:', err);
      setError('Errore nell\'eliminazione delle associazioni.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllMembers = async () => {
    try {
      setMembersLoading(true);
      const token = localStorage.getItem('token');

      const response = await api.delete('/admin/members/bulk-delete', {
        data: {
          confirmCode: deleteAllMembersConfirmCode
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowDeleteAllMembersModal(false);
      setDeleteAllMembersConfirmCode('');
      setSuccessMessage(response.data.message || 'Tutti i soci sono stati eliminati con successo!');

      // Refresh members list
      fetchMembers();
    } catch (err) {
      console.error('Error deleting all members:', err);
      setError(err.response?.data?.message || 'Errore nell\'eliminazione dei soci.');
      setShowDeleteAllMembersModal(false);
      setDeleteAllMembersConfirmCode('');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const userData = {
        username: formData.username,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active
      };

      // Aggiungiamo la disciplina solo se il ruolo è un ruolo SDA
      if (isSdaRole(formData.role) && formData.discipline) {
        userData.discipline = formData.discipline;
      } else if (!isSdaRole(formData.role)) {
        // Se non è un ruolo SDA, rimuoviamo la disciplina se presente
        userData.discipline = null;
      }

      // Only include password if it was provided
      if (formData.password) {
        userData.password = formData.password;
      }

      const response = await api.put(`/admin/users/${currentUser.id}`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('User updated:', response.data);
      setShowEditModal(false);
      setSuccessMessage('Utente aggiornato con successo!');

      // Refresh the appropriate list based on active tab
      if (activeTab === 'users') {
        fetchUsers();
      } else if (activeTab === 'associations') {
        fetchAssociations();
      }
    } catch (err) {
      console.error('Error updating user:', err);
      setError('Errore nell\'aggiornamento dell\'utente.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!formData.password || formData.password.length < 6) {
      setFormErrors({ password: 'La password deve essere di almeno 6 caratteri.' });
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setFormErrors({ confirmPassword: 'Le password non corrispondono.' });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      await api.post(`/admin/users/${currentUser.id}/reset-password`, {
        password: formData.password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowResetPasswordModal(false);
      setSuccessMessage('Password reimpostata con successo!');
    } catch (err) {
      console.error('Error resetting password:', err);
      setError('Errore nel reset della password.');
    } finally {
      setLoading(false);
    }
  };

  const isAssociation = (user) => {
    return user.role === 'association';
  };

  const renderPagination = () => (
    <div className="d-flex justify-content-center mt-4">
      <Button
        variant="outline-secondary"
        onClick={() => handlePageChange(Math.max(page - 1, 1))}
        disabled={page === 1}
        className="me-2"
      >
        &laquo; Precedente
      </Button>

      <span className="align-self-center mx-2">
        Pagina {page} di {totalPages || 1}
      </span>

      <Button
        variant="outline-secondary"
        onClick={() => handlePageChange(page < totalPages ? page + 1 : page)}
        disabled={page >= totalPages}
        className="ms-2"
      >
        Successiva &raquo;
      </Button>
    </div>
  );

  // Render associations pagination
  const renderAssociationsPagination = () => (
    <div className="d-flex justify-content-center mt-4">
      <Button
        variant="outline-secondary"
        onClick={() => handleAssociationsPageChange(Math.max(associationsPage - 1, 1))}
        disabled={associationsPage === 1}
        className="me-2"
      >
        &laquo; Precedente
      </Button>

      <span className="align-self-center mx-2">
        Pagina {associationsPage} di {associationsTotalPages || 1}
      </span>

      <Button
        variant="outline-secondary"
        onClick={() => handleAssociationsPageChange(associationsPage < associationsTotalPages ? associationsPage + 1 : associationsPage)}
        disabled={associationsPage >= associationsTotalPages}
        className="ms-2"
      >
        Successiva &raquo;
      </Button>
    </div>
  );

  // Export data functions (XLSX)
  const exportToXLSX = (data, filename) => {
    try {
      if (!Array.isArray(data) || data.length === 0) return;
      const headers = Object.keys(data[0]);
      const rows = data.map(row => headers.map(h => row[h] ?? ''));
      rows.unshift(headers);
      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Export');
      XLSX.writeFile(workbook, filename);
    } catch (err) {
      console.error('Export XLSX error', err);
    }
  };

  const handleExportMembers = () => {
    if (members && members.length > 0) {
      exportToXLSX(members, 'membri_esportati.xlsx');
      setSuccessMessage('Membri esportati con successo');
    } else {
      setError('Nessun membro da esportare');
    }
  };

  const handleExportAssociations = () => {
    if (associations && associations.length > 0) {
      exportToXLSX(associations, 'associazioni_esportate.xlsx');
      setSuccessMessage('Associazioni esportate con successo');
    } else {
      setError('Nessuna associazione da esportare');
    }
  };

  // View member details
  const openMemberDetail = (member) => {
    setCurrentMember(member);
    setShowMemberDetail(true);
  };

  // View association details
  const openAssociationDetail = (association) => {
    setCurrentAssociation(association);
    setShowAssociationDetail(true);
  };

  // Handle pagination based on active tab
  const handleMembersPageChange = (newPage) => {
    setMembersPage(newPage);
  };

  // Handle associations pagination
  const handleAssociationsPageChange = (newPage) => {
    setAssociationsPage(newPage);
  };

  // Main component render
  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex justify-content-between align-items-center">
            <h2 style={{ color: "#006e39" }}>Gestione Utenti e Soci</h2>
            <div>
              {/* Pulsanti per la tab Utenti */}
              {activeTab === 'users' && (
                <Button
                  onClick={openCreateModal}
                  style={{
                    backgroundColor: "#006e39",
                    borderColor: "#006e39"
                  }}
                  className="me-2"
                >
                  <i className="bi bi-person-plus me-2"></i>
                  Nuovo Utente
                </Button>
              )}

              {/* Pulsanti per la tab Associazioni */}
              {activeTab === 'associations' && (
                <>
                  <Button
                    onClick={handleOpenAssociationImport}
                    style={{
                      backgroundColor: "#006e39",
                      borderColor: "#006e39"
                    }}
                    className="me-2"
                  >
                    <i className="bi bi-file-earmark-arrow-up me-2"></i>
                    Importa Associazioni
                  </Button>

                  <Button
                    onClick={() => setShowDeleteAllAssociationsModal(true)}
                    style={{
                      backgroundColor: "#dc3545",
                      borderColor: "#dc3545"
                    }}
                  >
                    <i className="bi bi-trash me-2"></i>
                    Elimina Associazioni
                  </Button>
                </>
              )}

              {/* Pulsanti per la tab Soci */}
              {activeTab === 'members' && (
                <>
                  <Button
                    onClick={handleOpenMemberImport}
                    style={{
                      backgroundColor: "#006e39",
                      borderColor: "#006e39"
                    }}
                    className="me-2"
                  >
                    <i className="bi bi-file-earmark-arrow-up me-2"></i>
                    Importa Soci
                  </Button>
                  <Button
                    onClick={() => setShowDeleteAllMembersModal(true)}
                    style={{
                      backgroundColor: "#dc3545",
                      borderColor: "#dc3545"
                    }}
                  >
                    <i className="bi bi-trash me-2"></i>
                    Elimina Tutti i Soci
                  </Button>
                </>
              )}
            </div>
          </div>
          <p className="text-muted">Gestisci utenti e soci della piattaforma</p>
        </Col>
      </Row>

      {successMessage && (
        <Row>
          <Col>
            <Alert variant="success" onClose={() => setSuccessMessage('')} dismissible>
              {successMessage}
            </Alert>
          </Col>
        </Row>
      )}

      {error && (
        <Row>
          <Col>
            <Alert variant="danger" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      <Row>
        <Col>
          <div className="bg-white rounded shadow-sm p-4">
            <Tabs
              id="management-tabs"
              activeKey={activeTab}
              onSelect={(k) => {
                setActiveTab(k);
                setPage(1); // Reset pagination when changing tabs
                setMembersPage(1); // Reset members pagination
                setAssociationsPage(1); // Reset associations pagination
              }}
              className="mb-3"
              style={{ borderBottom: "2px solid #006e39", fontWeight: "bold" }}
            >
              {/* Tab Utenti - visibile solo ad admin, uisp_national e uisp_regional */}
              {user && (user.role === 'admin' || user.role === 'uisp_national' || user.role === 'uisp_regional') && (
                <Tab eventKey="users" title="Utenti">
                {loading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" variant="success">
                      <span className="visually-hidden">Caricamento...</span>
                    </Spinner>
                  </div>
                ) : users && users.length > 0 ? (
                  <>
                    {/* Enhanced Search Bar */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <Card.Body>
                        <Row>
                          <Col md={8}>
                            <InputGroup size="lg">
                              <InputGroup.Text style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                                <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                              </InputGroup.Text>
                              <Form.Control
                                placeholder="Cerca per username, email o ruolo..."
                                value={userSearch}
                                onChange={(e) => setUserSearch(e.target.value)}
                                style={{ borderColor: '#006e39' }}
                              />
                              {userSearch && (
                                <Button
                                  variant="outline-danger"
                                  onClick={() => setUserSearch('')}
                                  title="Cancella ricerca"
                                >
                                  <i className="bi bi-x-lg"></i>
                                </Button>
                              )}
                            </InputGroup>
                          </Col>
                          <Col md={4} className="d-flex align-items-center">
                            <div className="text-muted">
                              <i className="bi bi-funnel me-2"></i>
                              {totalUsers} utenti trovati
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <Table responsive striped hover>
                      <thead className="table-success">
                        <tr>
                          <th>Username</th>
                          <th>Email</th>
                          <th>Ruolo</th>
                          <th>Stato</th>
                          <th className="text-center">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user) => (
                            <tr key={user.id}>
                              <td>{user.username}</td>
                              <td>{user.email}</td>
                              <td>
                                {roleOptions.find(r => r.value === user.role)?.label || user.role}
                                {isSdaRole(user.role) && user.discipline && (
                                  <div><small className="text-muted">Disciplina: {user.discipline}</small></div>
                                )}
                              </td>
                              <td>
                                <Badge bg={user.is_active ? 'success' : 'danger'}>
                                  {user.is_active ? 'Attivo' : 'Disattivato'}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => openEditModal(user)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => openDeleteModal(user)}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                                <Button
                                  variant="outline-warning"
                                  size="sm"
                                  onClick={() => openResetPasswordModal(user)}
                                >
                                  <i className="bi bi-key"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>

                    {renderPagination()}
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-person-lines-fill fs-1 text-muted"></i>
                    <p className="mt-3">Nessun utente trovato.</p>
                  </div>
                )}
              </Tab>
              )}

              <Tab eventKey="associations" title="Associazioni">
                {associationsLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" variant="success">
                      <span className="visually-hidden">Caricamento...</span>
                    </Spinner>
                  </div>
                ) : associations && associations.length > 0 ? (
                  <>
                    {/* Enhanced Search Bar for Associations */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <Card.Body>
                        <Row>
                          <Col md={8}>
                            <InputGroup size="lg">
                              <InputGroup.Text style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                                <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                              </InputGroup.Text>
                              <Form.Control
                                placeholder="Cerca per nome, email, codice o comitato..."
                                value={associationSearch}
                                onChange={(e) => setAssociationSearch(e.target.value)}
                                style={{ borderColor: '#006e39' }}
                              />
                              {associationSearch && (
                                <Button
                                  variant="outline-danger"
                                  onClick={() => setAssociationSearch('')}
                                  title="Cancella ricerca"
                                >
                                  <i className="bi bi-x-lg"></i>
                                </Button>
                              )}
                            </InputGroup>
                          </Col>
                          <Col md={4} className="d-flex align-items-center">
                            <div className="text-muted">
                              <i className="bi bi-funnel me-2"></i>
                              {totalAssociations} associazioni trovate
                            </div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <Table responsive striped hover>
                      <thead className="table-success">
                        <tr>
                          <th>Nome</th>
                          <th>Email</th>
                          <th>Codice Affiliata</th>
                          <th>Comitato</th>
                          <th>Stato</th>
                          <th className="text-center">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {associations.map((assoc) => (
                            <tr key={assoc.id}>
                              <td>{assoc.name || assoc.username}</td>
                              <td>{assoc.email}</td>
                              <td>{assoc.association_code || '-'}</td>
                              <td>{assoc.committee || '-'}</td>
                              <td>
                                <Badge bg={assoc.is_active ? 'success' : 'danger'}>
                                  {assoc.is_active ? 'Attiva' : 'Disattivata'}
                                </Badge>
                              </td>
                              <td className="text-center">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => openAssociationDetail(assoc)}
                                >
                                  <i className="bi bi-eye"></i>
                                </Button>
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => handleOpenAssociationEdit(assoc)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => openDeleteModal(assoc)}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>

                    {renderAssociationsPagination()}
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-building fs-1 text-muted"></i>
                    <p className="mt-3">Nessuna associazione trovata.</p>
                  </div>
                )}
              </Tab>

              <Tab eventKey="members" title="Soci">
                {membersLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" role="status" variant="success">
                      <span className="visually-hidden">Caricamento...</span>
                    </Spinner>
                  </div>
                ) : members && members.length > 0 ? (
                  <>
                    {/* Enhanced Search Bar for Members */}
                    <Card className="mb-4 border-0 shadow-sm">
                      <Card.Body>
                        <Row>
                          <Col md={8}>
                            <InputGroup size="lg">
                              <InputGroup.Text style={{ backgroundColor: '#f8f9fa', borderColor: '#006e39' }}>
                                <i className="bi bi-search" style={{ color: '#006e39' }}></i>
                              </InputGroup.Text>
                              <Form.Control
                                placeholder="Cerca per nome, cognome, codice fiscale o associazione..."
                                value={memberSearch}
                                onChange={(e) => setMemberSearch(e.target.value)}
                                style={{ borderColor: '#006e39' }}
                              />
                              {memberSearch && (
                                <Button
                                  variant="outline-danger"
                                  onClick={() => setMemberSearch('')}
                                  title="Cancella ricerca"
                                >
                                  <i className="bi bi-x-lg"></i>
                                </Button>
                              )}
                            </InputGroup>
                          </Col>
                          <Col md={4} className="d-flex align-items-center justify-content-between">
                            <div className="text-muted">
                              <i className="bi bi-funnel me-2"></i>
                              {totalMembers} soci trovati
                            </div>
                            <Button
                              onClick={handleExportMembers}
                              size="sm"
                              style={{
                                backgroundColor: "#006e39",
                                borderColor: "#006e39"
                              }}
                            >
                              <i className="bi bi-file-earmark-arrow-down me-2"></i>
                              Esporta
                            </Button>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    <Table responsive striped hover>
                      <thead className="table-success">
                        <tr>
                          <th>Nome</th>
                          <th>Cognome</th>
                          <th>Codice Fiscale</th>
                          <th>Associazione</th>
                          <th className="text-center">Azioni</th>
                        </tr>
                      </thead>
                      <tbody>
                        {members.map((member) => (
                            <tr key={member.id}>
                              <td>{member.first_name}</td>
                              <td>{member.last_name}</td>
                              <td>{member.fiscal_code}</td>
                              <td>{member.association_name || member.association_code || '-'}</td>
                              <td className="text-center">
                                <Button
                                  variant="outline-info"
                                  size="sm"
                                  className="me-2"
                                  onClick={() => openMemberDetail(member)}
                                >
                                  <i className="bi bi-eye"></i>
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={() => {
                                    if (window.confirm(`Sei sicuro di voler eliminare ${member.first_name} ${member.last_name}?`)) {
                                      handleDeleteMember(member.id);
                                    }
                                  }}
                                >
                                  <i className="bi bi-trash"></i>
                                </Button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </Table>

                    <div className="d-flex justify-content-center mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => handleMembersPageChange(Math.max(membersPage - 1, 1))}
                        disabled={membersPage === 1}
                        className="me-2"
                      >
                        &laquo; Precedente
                      </Button>

                      <span className="align-self-center mx-2">
                        Pagina {membersPage} di {membersTotalPages || 1}
                      </span>

                      <Button
                        variant="outline-secondary"
                        onClick={() => handleMembersPageChange(membersPage < membersTotalPages ? membersPage + 1 : membersPage)}
                        disabled={membersPage >= membersTotalPages}
                        className="ms-2"
                      >
                        Successiva &raquo;
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <i className="bi bi-people-fill fs-1 text-muted"></i>
                    <p className="mt-3">Nessun socio trovato.</p>
                  </div>
                )}
              </Tab>
            </Tabs>
          </div>
        </Col>
      </Row>

      {/* Member Detail Modal */}
      <Modal show={showMemberDetail} onHide={() => setShowMemberDetail(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Dettagli Socio</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentMember && (
            <div>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Nome:</strong> {currentMember.first_name}
                </Col>
                <Col md={6}>
                  <strong>Cognome:</strong> {currentMember.last_name}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Codice Fiscale:</strong> {currentMember.fiscal_code}
                </Col>
                <Col md={6}>
                  <strong>Data di Nascita:</strong> {currentMember.birth_date || '-'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Email:</strong> {currentMember.email || '-'}
                </Col>
                <Col md={6}>
                  <strong>Telefono:</strong> {currentMember.phone || '-'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Associazione:</strong> {currentMember.association_name || currentMember.association_code || '-'}
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowMemberDetail(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Association Detail Modal */}
      <Modal show={showAssociationDetail} onHide={() => setShowAssociationDetail(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Dettagli Associazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentAssociation && (
            <div>
              <Row className="mb-3">
                <Col md={12}>
                  <strong>Nome:</strong> {currentAssociation.name || currentAssociation.username}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Email:</strong> {currentAssociation.email}
                </Col>
                <Col md={6}>
                  <strong>Codice Affiliata:</strong> {currentAssociation.association_code || '-'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Comitato Regionale:</strong> {currentAssociation.regional_committee || '-'}
                </Col>
                <Col md={6}>
                  <strong>Comitato Territoriale:</strong> {currentAssociation.territorial_committee || '-'}
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Telefono:</strong> {currentAssociation.phone || '-'}
                </Col>
                <Col md={6}>
                  <strong>Stato:</strong>{' '}
                  <Badge bg={currentAssociation.is_active ? 'success' : 'danger'}>
                    {currentAssociation.is_active ? 'Attiva' : 'Disattivata'}
                  </Badge>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="warning" 
            onClick={() => {
              setShowAssociationDetail(false);
              openResetPasswordModal(currentAssociation);
            }}
            className="me-auto"
          >
            <i className="bi bi-key me-2"></i>
            Reset Password
          </Button>
          <Button variant="secondary" onClick={() => setShowAssociationDetail(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Create User Modal */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Crea Nuovo {activeTab === 'associations' ? 'Associazione' : 'Utente'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Username*</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                isInvalid={!!formErrors.username}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.username}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email*</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                isInvalid={!!formErrors.email}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password*</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                isInvalid={!!formErrors.password}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.password}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Conferma Password*</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                isInvalid={!!formErrors.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ruolo*</Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                isInvalid={!!formErrors.role}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formErrors.role}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Mostra il campo disciplina solo se è selezionato un ruolo SDA */}
            {isSdaRole(formData.role) && (
              <Form.Group className="mb-3">
                <Form.Label>Disciplina*</Form.Label>
                <Form.Select
                  name="discipline"
                  value={formData.discipline}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.discipline}
                >
                  <option value="">Seleziona una disciplina</option>
                  {disciplineOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {formErrors.discipline}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Il responsabile SDA potrà gestire solo eventi di questa disciplina.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Utente attivo"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Invia email con credenziali"
                name="sendEmail"
                checked={formData.sendEmail}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Se selezionato, verrà inviata un'email automatica all'utente con username e password.
              </Form.Text>
            </Form.Group>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreateModal(false)}>
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={handleCreateUser}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Crea'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit User Modal */}
      <Modal show={showEditModal} onHide={() => setShowEditModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Modifica {isAssociation(currentUser) ? 'Associazione' : 'Utente'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" className="mb-3">
              {error}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Username*</Form.Label>
              <Form.Control
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                isInvalid={!!formErrors.username}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.username}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Email*</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                isInvalid={!!formErrors.email}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.email}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Password (lasciare vuoto per non modificare)</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                isInvalid={!!formErrors.password}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.password}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Conferma Password</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                isInvalid={!!formErrors.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ruolo*</Form.Label>
              <Form.Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                isInvalid={!!formErrors.role}
              >
                {roleOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {formErrors.role}
              </Form.Control.Feedback>
            </Form.Group>

            {/* Mostra il campo disciplina solo se è selezionato un ruolo SDA */}
            {isSdaRole(formData.role) && (
              <Form.Group className="mb-3">
                <Form.Label>Disciplina*</Form.Label>
                <Form.Select
                  name="discipline"
                  value={formData.discipline}
                  onChange={handleInputChange}
                  isInvalid={!!formErrors.discipline}
                >
                  <option value="">Seleziona una disciplina</option>
                  {disciplineOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {formErrors.discipline}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  Il responsabile SDA potrà gestire solo eventi di questa disciplina.
                </Form.Text>
              </Form.Group>
            )}

            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                label="Utente attivo"
                name="is_active"
                checked={formData.is_active}
                onChange={handleInputChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditModal(false)}>
            Annulla
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateUser}
            disabled={loading}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Salva Modifiche'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete User Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Eliminazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sei sicuro di voler eliminare l'utente <strong>{currentUser.username}</strong>?
          Questa azione non può essere annullata.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Annulla
          </Button>
          <Button variant="danger" onClick={handleDeleteUser} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Elimina'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reset Password Modal */}
      <Modal show={showResetPasswordModal} onHide={() => setShowResetPasswordModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reimposta Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nuova Password*</Form.Label>
              <Form.Control
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                isInvalid={!!formErrors.password}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.password}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Conferma Password*</Form.Label>
              <Form.Control
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                isInvalid={!!formErrors.confirmPassword}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.confirmPassword}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)}>
            Annulla
          </Button>
          <Button variant="primary" onClick={handleResetPassword} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Reimposta Password'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete All Associations Modal */}
      <Modal show={showDeleteAllAssociationsModal} onHide={() => setShowDeleteAllAssociationsModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Eliminazione Totale</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>ATTENZIONE!</strong> Stai per eliminare TUTTE le associazioni e i relativi utenti.
            Questa azione è irreversibile.
          </Alert>
          Sei sicuro di voler procedere?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteAllAssociationsModal(false)}>
            Annulla
          </Button>
          <Button variant="danger" onClick={handleDeleteAllAssociations} disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : 'Elimina Tutto'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete All Members Modal */}
      <Modal show={showDeleteAllMembersModal} onHide={() => {
        setShowDeleteAllMembersModal(false);
        setDeleteAllMembersConfirmCode('');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Conferma Eliminazione Totale Soci</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="danger">
            <strong>⚠️ ATTENZIONE!</strong> Stai per eliminare <strong>TUTTI I SOCI</strong> dal database.
            Questa azione è <strong>IRREVERSIBILE</strong> e cancellerà permanentemente tutti i dati dei soci.
          </Alert>
          <p className="mb-3">
            Per confermare l'eliminazione, digita il codice di conferma:
          </p>
          <div className="text-center mb-3">
            <Badge bg="dark" className="fs-5 p-2">ELIMINA_TUTTI</Badge>
          </div>
          <Form.Group>
            <Form.Label>Codice di Conferma*</Form.Label>
            <Form.Control
              type="text"
              placeholder="Digita: ELIMINA_TUTTI"
              value={deleteAllMembersConfirmCode}
              onChange={(e) => setDeleteAllMembersConfirmCode(e.target.value)}
              className="text-center"
            />
            <Form.Text className="text-muted">
              {totalMembers} soci verranno eliminati definitivamente
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => {
              setShowDeleteAllMembersModal(false);
              setDeleteAllMembersConfirmCode('');
            }}
          >
            Annulla
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteAllMembers} 
            disabled={membersLoading || deleteAllMembersConfirmCode !== 'ELIMINA_TUTTI'}
          >
            {membersLoading ? <Spinner animation="border" size="sm" /> : 'Elimina Tutti i Soci'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Association Import Modal */}
      <AssociationImportModal
        show={showAssociationImportModal}
        handleClose={() => setShowAssociationImportModal(false)}
        onImportSuccess={handleAssociationImportSuccess}
      />

      {/* Member Import Modal */}
      <MemberImportModal
        show={showMemberImportModal}
        handleClose={() => setShowMemberImportModal(false)}
        onImportSuccess={handleMemberImportSuccess}
      />

      {/* Association Creation/Edit Modal */}
      <AssociationCreationModal
        show={showAssociationCreateModal}
        onHide={() => setShowAssociationCreateModal(false)}
        onSuccess={handleAssociationCreateSuccess}
        association={currentEditAssociation}
      />
    </Container>
  );
};

export default UserManagement;
